import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Calculator, Download, Wallet } from "lucide-react";
import { validatePayrollPeriod } from "@/lib/business-validation";

export default function Payroll() {
  const [periods, setPeriods] = useState([]);
  const [selPid, setSelPid] = useState(null);
  const [lines, setLines] = useState([]);
  const [open, setOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    code: new Date().toISOString().slice(0,7),
    from_date: new Date().toISOString().slice(0,8) + "01",
    to_date: new Date(new Date().getFullYear(), new Date().getMonth()+1, 0).toISOString().slice(0,10),
    bonus_pool_total: 50000000, bonus_a_pct: 50, bonus_b_pct: 30, bonus_c_pct: 20,
  });

  const loadPeriods = useCallback(() => api.get("/payroll/periods").then((r) => {
    setPeriods(r.data);
    if (!selPid && r.data[0]) setSelPid(r.data[0].id);
  }), [selPid]);
  useEffect(() => { loadPeriods(); }, [loadPeriods]);
  useEffect(() => { if (selPid) api.get(`/payroll/periods/${selPid}/lines`).then((r) => setLines(r.data)); }, [selPid]);

  const compute = async () => {
    try { await api.post(`/payroll/periods/${selPid}/compute`); toast.success("Đã tính lương + bonus A/B/C");
      const r = await api.get(`/payroll/periods/${selPid}/lines`); setLines(r.data);
    } catch { toast.error("Tính lương thất bại"); }
  };

  const create = async () => {
    try {
      const payload = {
        ...form,
        bonus_pool_total: Number(form.bonus_pool_total),
        bonus_a_pct: Number(form.bonus_a_pct),
        bonus_b_pct: Number(form.bonus_b_pct),
        bonus_c_pct: Number(form.bonus_c_pct),
      };
      const validation = validatePayrollPeriod(payload);
      if (!validation.ok) {
        setFieldErrors(validation.fieldErrors);
        toast.error(validation.message);
        return;
      }
      const r = await api.post("/payroll/periods", payload);
      toast.success("Tạo kỳ lương"); setOpen(false); await loadPeriods(); setSelPid(r.data.id);
    } catch (e) {
      const parsed = parseApiError(e, "Lỗi");
      setFieldErrors(parsed.fieldErrors);
      toast.error(parsed.message);
    }
  };

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFieldErrors((errors) => ({ ...errors, [field]: undefined, bonus_total_pct: undefined }));
  };

  const exportMisa = () => {
    const token = localStorage.getItem("ts_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payroll/periods/${selPid}/export-misa`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then((r) => r.blob()).then((b) => {
      const u = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = `bang-luong-misa-${selPid?.slice(0,8)}.xlsx`;
      a.click(); URL.revokeObjectURL(u);
      toast.success("Đã tải file Misa");
    });
  };

  const cur = periods.find((p) => p.id === selPid);
  const totalNet = lines.reduce((a, l) => a + Number(l.net_pay || 0), 0);

  return (
    <div data-testid="page-payroll">
      <PageHeader
        kicker="HR-Ops" title="Lương & Thưởng A/B/C" breadcrumb={["HR-Ops", "Payroll"]}
        actions={
          <>
            <Button onClick={() => { setFieldErrors({}); setOpen(true); }} variant="outline" className="rounded-sm" data-testid="btn-create-period">
              <Plus className="w-4 h-4 mr-1.5" />Kỳ lương mới
            </Button>
            <Button onClick={compute} disabled={!selPid} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-compute">
              <Calculator className="w-4 h-4 mr-1.5" />Tính lương
            </Button>
            <Button onClick={exportMisa} disabled={!lines.length} variant="outline" className="rounded-sm border-amber-600 text-amber-900" data-testid="btn-export-misa">
              <Download className="w-4 h-4 mr-1.5" />Export Misa
            </Button>
          </>
        }
      />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="bg-white border border-stone-200 rounded-sm p-4 flex flex-wrap gap-4 items-end">
          <div className="min-w-[200px]">
            <Label className="text-xs">Kỳ lương</Label>
            <Select value={selPid || undefined} onValueChange={setSelPid}>
              <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Chọn kỳ" /></SelectTrigger>
              <SelectContent>{periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} ({fmtDate(p.from_date)}–{fmtDate(p.to_date)})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {cur && (
            <>
              <Info label="Trạng thái" value={cur.status} />
              <Info label="Pool A/B/C" value={fmtMoney(cur.bonus_pool_total)} />
              <Info label="Tỷ lệ" value={`A:${cur.bonus_a_pct}% • B:${cur.bonus_b_pct}% • C:${cur.bonus_c_pct}%`} />
              <Info label="Tổng net pay" value={fmtMoney(totalNet)} accent="emerald" />
            </>
          )}
        </div>

        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  {["Mã","Họ tên","Lương cơ bản","Phút làm","KPI","KPI Bonus","Rank","Bonus A/B/C","Khấu trừ","Thực lĩnh"].map((h) => (
                    <th key={h} className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {lines.map((l) => (
                  <tr key={l.id} className="hover:bg-stone-50" data-testid={`row-payroll-${l.emp_code}`}>
                    <td className="px-3 py-2 font-mono text-xs text-stone-600">{l.emp_code}</td>
                    <td className="px-3 py-2 font-semibold">{l.emp_name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-right">{fmtMoney(l.base_salary)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-right">{l.worked_minutes}</td>
                    <td className="px-3 py-2 font-mono text-xs">{Number(l.kpi_score).toFixed(1)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-right text-emerald-800">{fmtMoney(l.kpi_bonus)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-sm font-bold ${
                        l.abc_rank === "A" ? "bg-emerald-100 text-emerald-900" :
                        l.abc_rank === "B" ? "bg-amber-100 text-amber-900" :
                        "bg-stone-100 text-stone-800"}`}>{l.abc_rank || "—"}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-right text-amber-800">{fmtMoney(l.abc_bonus)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-right text-red-700">{fmtMoney(l.deductions)}</td>
                    <td className="px-3 py-2 font-mono text-sm text-right font-bold">{fmtMoney(l.net_pay)}</td>
                  </tr>
                ))}
                {lines.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-stone-400">
                  Chưa có dữ liệu. Bấm <Wallet className="w-3 h-3 inline" /> <strong>Tính lương</strong> để compute từ chấm công + KPI.
                </td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Tạo kỳ lương mới</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mã kỳ" value={form.code} error={fieldErrors.code} onChange={(v) => setField("code", v)} />
            <Field label="Quỹ thưởng A/B/C (VNĐ)" type="number" value={form.bonus_pool_total} error={fieldErrors.bonus_pool_total} onChange={(v) => setField("bonus_pool_total", v)} testid="f-pool" />
            <Field label="Từ ngày" type="date" value={form.from_date} error={fieldErrors.from_date} onChange={(v) => setField("from_date", v)} />
            <Field label="Đến ngày" type="date" value={form.to_date} error={fieldErrors.to_date} onChange={(v) => setField("to_date", v)} />
            <Field label="Tỷ lệ A%" type="number" value={form.bonus_a_pct} error={fieldErrors.bonus_a_pct || fieldErrors.bonus_total_pct} onChange={(v) => setField("bonus_a_pct", v)} />
            <Field label="Tỷ lệ B%" type="number" value={form.bonus_b_pct} error={fieldErrors.bonus_b_pct} onChange={(v) => setField("bonus_b_pct", v)} />
            <Field label="Tỷ lệ C%" type="number" value={form.bonus_c_pct} error={fieldErrors.bonus_c_pct} onChange={(v) => setField("bonus_c_pct", v)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Hủy</Button>
            <Button onClick={create} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-save-period">Tạo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">{label}</div>
      <div className={`font-semibold ${accent === "emerald" ? "text-emerald-800" : ""}`}>{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, error }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(error)}
        className={`h-9 rounded-sm mt-1 ${error ? "border-red-400 focus-visible:ring-red-500" : ""}`}
        data-testid={testid}
      />
      {error && <div className="text-xs text-red-700 mt-1">{error}</div>}
    </div>
  );
}
